import type { components } from "@/api/contract";
import type { RunState, RunStatus, StreamPhase, ToolState } from "../types";
import type { AttachmentView } from "./attachments";
import type { Source } from "./sseEvents";

export type Message = components["schemas"]["Message"];

/**
 * A renderable thread row: either a persisted server message (the active path,
 * already resolved by the backend) or a live optimistic row from the in-flight
 * run. Stable `key`s keep React reconciliation calm as live rows resolve to
 * server rows on completion.
 */
export type ThreadMessage =
  | { kind: "server"; key: string; message: Message }
  | { kind: "liveUser"; key: string; content: string; attachments: AttachmentView[] }
  | {
      kind: "liveAssistant";
      key: string;
      content: string;
      sources: Source[] | null;
      tools: ToolState[];
      phase: StreamPhase | null;
      status: RunStatus;
    };

/** Whether the given run should render into the view for `conversationId`. */
export function runOwnsView(run: RunState | null, conversationId: string | null): boolean {
  if (!run) return false;
  if (conversationId === null) return run.isNew;
  return run.conversationId === conversationId;
}

/**
 * Merge the server's active-path messages with the in-flight run into the final
 * render list. In `append` mode the optimistic user turn is added if the server
 * copy hasn't arrived yet; in `regenerate` mode the trailing assistant turn is
 * dropped and replaced by the streaming one. With no live run, it's just the
 * server messages.
 */
export function buildThread(
  serverMessages: Message[],
  run: RunState | null,
  conversationId: string | null,
): ThreadMessage[] {
  const base: ThreadMessage[] = serverMessages.map((message) => ({
    kind: "server",
    key: message.id,
    message,
  }));

  if (!run || !runOwnsView(run, conversationId)) return base;

  // `finalize()` deliberately keeps the optimistic run visible while it
  // refetches the persisted conversation. Once that refetch contains the exact
  // assistant message announced by the run's `done` event, rendering the live
  // row as well would briefly duplicate its text/tool cards. Hand ownership to
  // the server row immediately; the provider will clear the run after the same
  // refetch completes.
  const assistantPersisted =
    run.outputMessageId !== null &&
    serverMessages.some((message) => message.id === run.outputMessageId);
  if (assistantPersisted) return base;

  // A generation tool persists its assistant placeholder before the run is
  // done. On a new conversation, the navigation-triggered detail query can
  // therefore return that placeholder while the same call is still owned by
  // the live SSE row. Keep the live version (it has the freshest lifecycle
  // state), but remove the server copy carrying the same tool-call id.
  const liveToolCallIds = new Set(run.tools.map((tool) => tool.callId));
  const optimisticBase =
    liveToolCallIds.size === 0
      ? base
      : base.filter(
          (item) =>
            !(
              item.kind === "server" &&
              item.message.role === "assistant" &&
              item.message.tool_calls?.some((call) => liveToolCallIds.has(call.id))
            ),
        );

  const liveAssistant: ThreadMessage = {
    kind: "liveAssistant",
    key: "live-assistant",
    content: run.assistantText,
    sources: run.sources,
    tools: run.tools,
    phase: run.phase,
    status: run.status,
  };

  if (run.mode === "regenerate") {
    const parentIndex = run.parentMessageId
      ? optimisticBase.findIndex(
          (item) => item.kind === "server" && item.message.id === run.parentMessageId,
        )
      : -1;
    const kept =
      parentIndex === -1 ? optimisticBase : optimisticBase.slice(0, parentIndex + 1);
    return [...kept, liveAssistant];
  }

  const items = [...optimisticBase];
  const userPersisted =
    run.userMessageId !== null && serverMessages.some((m) => m.id === run.userMessageId);
  if (!userPersisted) {
    items.push({
      kind: "liveUser",
      key: "live-user",
      content: run.userText,
      attachments: run.userAttachments,
    });
  }
  items.push(liveAssistant);
  return items;
}

/**
 * The leaf message id of the sibling `offset` positions from a message's active
 * variant, or null if out of range / no siblings. Drives the branch switcher's
 * ◄ ► arrows via the message's `variant_*` annotations.
 */
export function siblingLeafForOffset(message: Message, offset: -1 | 1): string | null {
  const ids = message.variant_leaf_message_ids;
  const count = message.variant_count ?? 0;
  const index = message.variant_index ?? 0;
  if (!ids || count < 2) return null;
  const target = index + offset;
  if (target < 0 || target >= ids.length) return null;
  return ids[target] ?? null;
}
